import { createPortal } from 'react-dom'
import { ErrorBoundary } from 'react-error-boundary'
import { isEmpty, pipe } from 'remeda'
import { AlgoliaFilters } from '@components/AlgoliaSearch/AlgoliaFilters'
import ApparelMacroCategory from '@components/ApparelMacroCategory/ApparelMacroCategory'
import { CartDrawer } from '@components/CartDrawer/CartDrawer'
import Header from '@components/Header'
import renderLiquidLinks from '@components/Header/utils/renderLiquidLinks'
import { VisualTopImage } from '@components/VisualTopImage/VisualTopImage'
import type { VisualTopImageProps } from '@components/VisualTopImage/VisualTopImage.types'
import {
  withAlgoliaSearch,
  withApollo,
  withErrorBoundary,
  withExchangeRates,
  withI18next,
  withSkeletonTheme,
  withStaffPickImage,
  withTheme,
} from '@hocs'
import type { CartDrawerData, CollectionMessageData } from '@velasca/types'
import { CollectionTextSection } from '@velasca/ui/collection-text-section'
import { ErrorFallback } from '@velasca/ui/error-fallback'
import { $settings } from '@velasca/utils/globals'
import { parseJSONSafe } from '@velasca/utils/parse-json-safe'

const apparelMacroCategoryElJson = document.getElementById(
  'apparel-macro-category-json',
)

interface IApparelMacroCategoryAppProps {
  apparelMacroCategoryEl: HTMLElement
  visualTopImageData: VisualTopImageProps
  cartDrawerData: CartDrawerData
  headerEl: HTMLElement | null
  headerData: any
  promotionalBannerData: any
  productLocaleElJson: any
  collectionTopMessageData: CollectionMessageData
}

// TODO: spostare tutta questa parte direttamente dentro l'ApparelMacroCategory
const ApparelMacroCategoryApp = ({
  apparelMacroCategoryEl,
  visualTopImageData,
  cartDrawerData,
  headerEl,
  headerData,
  promotionalBannerData,
  productLocaleElJson,
  collectionTopMessageData,
}: IApparelMacroCategoryAppProps) => (
  <>
    <CartDrawer emptyCartCtas={cartDrawerData?.ctas || []} />
    {headerEl &&
      createPortal(
        <Header
          sticky
          promotionalBanner={promotionalBannerData || {}}
          miscHeaderData={headerData?.miscData}
          menu={
            renderLiquidLinks(
              headerData?.nav,
              headerData?.miscData?.liquidLinks,
            ) ?? undefined
          }
          lowerMenu={
            headerData?.mobileOnlyNav &&
            renderLiquidLinks(
              headerData?.mobileOnlyNav,
              headerData?.miscData?.liquidLinks,
            )
          }
        />,
        headerEl,
      )}

    {visualTopImageData && (
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        <VisualTopImage
          title={visualTopImageData.title}
          mobileImage={visualTopImageData.mobileImage}
          desktopImage={visualTopImageData.desktopImage}
        />
      </ErrorBoundary>
    )}

    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ErrorBoundary FallbackComponent={ErrorFallback}>
        {$settings.algolia_enable_filters && <AlgoliaFilters />}
      </ErrorBoundary>
      {collectionTopMessageData && !isEmpty(collectionTopMessageData) && (
        <CollectionTextSection
          title={collectionTopMessageData.title}
          upperTitle={collectionTopMessageData.upperTitle}
          paragraph={collectionTopMessageData.paragraph}
        />
      )}
      <ApparelMacroCategory
        //TODO: parsare i JSON fuori, nel renderApparelMacroCollectionApp
        categories={parseJSONSafe(apparelMacroCategoryElJson?.innerText ?? '')}
        showCollectionBannerOnMobile={parseJSONSafe(
          apparelMacroCategoryEl.getAttribute(
            'data-show-collection-banner-on-mobile',
          ) ?? 'false',
        )}
        productsCount={parseJSONSafe(
          apparelMacroCategoryEl.getAttribute('data-products-count') ?? '[]',
        )}
        collectionAltStringTemplate={
          productLocaleElJson?.product?.collectionAltStringTemplate
        }
        recentProductsSettings={{
          enabled: $settings.recentProductsCollectionEnabled || false,
        }}
      />
    </ErrorBoundary>
  </>
)

export const ConnectedApparelMacroCategoryApp = pipe(
  ApparelMacroCategoryApp,
  withAlgoliaSearch, // TODO: wrappare condizionalmente questo HOC in base ai settings
  withStaffPickImage,
  withExchangeRates,
  withSkeletonTheme,
  withI18next,
  withApollo,
  withErrorBoundary,
  withTheme,
)
